import { type QueryResult } from '@apollo/client'
import { useDeepMemo } from '@retailer-platform/shared-common/src/react/react.hooks'
import { isEqual } from 'lodash-es'
import { useEffect } from 'react'
import { type ApolloError, type QueryHookOptions } from '@apollo/client'
import { useLocalStorageKeyDirect } from '../local-storage/useLocalStorageKeyDirect.hooks'

export type QueryHook<TData, TVariables> = (
  options?: QueryHookOptions<TData, TVariables>
) => QueryResult<TData, TVariables>

/**
 * Helper type to extract TData from a query hook
 */
type ExtractQueryData<T> = T extends (options?: QueryHookOptions<infer TData, any>) => any
  ? TData
  : never

// Cache is namespaced, in case we need to fully clear it without touching other keys
const cacheNamespace = 'staleWhileRevalidate'
// This can be useful to invalidate cache -- recommended to bump if underlying data changes
const cacheVersion = 'v1'
// Maximum size for a single cache entry (200KB) - prevents large responses from filling IndexedDB
const MAX_CACHE_ENTRY_BYTES = 200 * 1024
// Default maximum age for cache entries (3 days) - entries older than this are treated as expired
const DEFAULT_MAX_CACHE_AGE_MS = 3 * 24 * 60 * 60 * 1000

/**
 * Wrapper type for cached data that includes timestamp for TTL checking
 */
type CachedEntry<TData> = {
  data: TData
  cachedAt: number
}

export type StaleWhileRevalidateOptions = {
  baseCacheKey: string
  /**
   * Maximum age for cache entries in milliseconds.
   * Entries older than this are treated as expired.
   * @default 3 days (259200000ms)
   */
  maxCacheAgeMs?: number
}

/**
 * This will receive a query hook and add localstorage caching functionality to it.
 * Note that this will still call the underlying resource, and automatically update the cache once it gets
 * the new information.
 *
 * This will change the return slightly based on whether the item is in cache or not. It introduces a new key called
 * `errorWhileRevalidating` that will contain the contents of `error` when the item is in cache.
 *
 * This allows the consumer to do something different (e.g. showing a stale data warning) instead of fully erroring out.
 * For more details on how the return changes, refer to the following table:
 *
 * | in cache                     | data                 | loading  | error     | errorWhileRevalidating | ...rest  |
 * |------------------------------|----------------------|----------|-----------|------------------------|----------|
 * | unknown (loading cache data) | undefined            | true     | undefined | undefined              | original |
 * | yes                          | original || cached   | false    | undefined | original               | original |
 * | no (or skipCache: true)      | original             | original | original  | undefined              | original |
 *
 * @param useQueryHook
 * @param options
 * @returns
 */
export const makeQueryStaleWhileRevalidate =
  <THook extends QueryHook<any, any>>(useQueryHook: THook, options: StaleWhileRevalidateOptions) =>
  (
    queryOptions: Parameters<THook>[0],
    cacheOptions?: {
      skipCache?: boolean
    }
  ): ReturnType<THook> & {
    /**
     * Will contain the error from this request as long as there was an error and
     * this request was cached. This is meant so the normal error can be 'swallowed'
     * while still allowing us to act on it.
     */
    errorWhileRevalidating?: ApolloError
  } => {
    type TData = ExtractQueryData<THook>
    type THookResult = ReturnType<THook> & { errorWhileRevalidating?: ApolloError }

    const cacheKey = useDeepMemo(
      () =>
        `${cacheNamespace}:${cacheVersion}:${options.baseCacheKey}:${JSON.stringify(
          queryOptions.variables
        )}`,
      // @ts-ignore, types for useDeepMemo are not right
      [queryOptions.variables]
    )

    const result = useQueryHook(queryOptions)

    // If Apollo already has data (cache hit), defer localstore load to requestIdleCallback
    // for housekeeping (cache updates, staleness checks). This avoids blocking the main
    // thread when the data is already available from Apollo's cache.

    // localStorage alternative - faster synchronous reads but limited storage
    const {
      data: cachedEntry,
      loading: cacheLoading,
      setData,
      dataRef: cachedEntryRef,
      hasKey,
    } = useLocalStorageKeyDirect<CachedEntry<TData>>(cacheKey, {
      deferLoad: !!result.data,
      maxEntryBytes: MAX_CACHE_ENTRY_BYTES,
    })

    // Check if cached entry is expired
    const maxCacheAgeMs = options.maxCacheAgeMs ?? DEFAULT_MAX_CACHE_AGE_MS
    const isExpired = cachedEntry && Date.now() - cachedEntry.cachedAt > maxCacheAgeMs
    const cachedData = isExpired ? undefined : cachedEntry?.data
    const hasValidCache = !isExpired && hasKey

    // Persist data to cache
    // Uses dataRef for comparison — the ref is always populated (even when deferLoad
    // skips setting React state), avoiding unnecessary re-renders and double reads.
    useEffect(() => {
      if (!result.error && result.data) {
        // Compare against ref (populated synchronously, no re-render in deferred path)
        const cached = cachedEntryRef.current
        if (cached) {
          const isEntryExpired = Date.now() - cached.cachedAt > maxCacheAgeMs
          // Skip write if data hasn't changed and cache isn't expired
          // Note: if expired, we still write to refresh cachedAt timestamp
          if (!isEntryExpired && isEqual(cached.data, result.data)) {
            return
          }
        }

        // Defer the write to idle time since the user already has fresh Apollo data
        // rendered. This avoids blocking the main thread with JSON.stringify and
        // cache writes right after a data update.
        // Size checking is handled by the storage hook via maxEntryBytes.
        const writeToCache = () => {
          setData({ data: result.data, cachedAt: Date.now() })
        }

        if (typeof requestIdleCallback === 'function') {
          const idleId = requestIdleCallback(writeToCache)
          return () => cancelIdleCallback(idleId)
        } else {
          // Fallback for browsers without requestIdleCallback support
          const timeoutId = setTimeout(writeToCache, 0)
          return () => clearTimeout(timeoutId)
        }
      }
    }, [setData, result.data, result.error, maxCacheAgeMs, cachedEntryRef])

    // If cache is not enabled, return results as-is
    if (cacheOptions?.skipCache) return result as THookResult

    return {
      ...result,
      // This will potentially swallow the error, as long as valid (non-expired) cache exists
      error: hasValidCache ? undefined : result.error,
      errorWhileRevalidating: hasValidCache ? result.error : undefined,
      // Return cached data, if there is data from origin, return that instead
      data: result.data || cachedData,
      // If Apollo already has data, we're done loading. Otherwise, wait for IndexedDB cache or Apollo.
      loading: result.data ? false : cacheLoading || (hasValidCache ? false : result.loading),
    } as THookResult
  }

/**
 * Deletes any SWR cache entries from localStorage whose version doesn't match
 * the current cacheVersion. This cleans up stale entries after a version bump.
 */
export const useQueryStaleWhileRevalidateCleanup = () => {
  useEffect(() => {
    try {
      const keysToDelete: string[] = []

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (!key) continue

        // Only look at keys in our namespace
        if (!key.startsWith(cacheNamespace)) continue

        // Delete if it doesn't match the current version prefix
        if (!key.startsWith(`${cacheNamespace}:${cacheVersion}:`)) {
          keysToDelete.push(key)
        }
      }

      keysToDelete.forEach(key => localStorage.removeItem(key))
    } catch {
      console.error('Failed to clean up SWR cache keys')
    }
  }, [])
}
